import React, { useState, useEffect, useRef } from 'react';
import { HiOutlineRefresh } from 'react-icons/hi';
import { getManagementPendingOrders, getManagementOrderById, getOrderAttachments, getClientById } from '../services/api';
import { refreshAccessToken } from '../services/auth';
import { formatDateTime, getStatusColor, handleApiError, SCRATCH_CARD_PRICES, EPIN_PRICES, calculateOrderTotal, getDiscountDetails } from '../utils/helpers';
import OrderDetailsView from './OrderDetailsView';
import DateRangePicker from '../components/common/DateRangePicker';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const SupplierOrderHistory = () => {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [totalOrders, setTotalOrders] = useState(0);
    const [totalFromAPI, setTotalFromAPI] = useState(0); // Store total from API
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage] = useState(20);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    // Attachments state for selected order
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [existingAttachmentsLoading, setExistingAttachmentsLoading] = useState(false);
    const [exportStartDate, setExportStartDate] = useState('');
    const [exportEndDate, setExportEndDate] = useState('');

    const [originalOrders, setOriginalOrders] = useState([]); // Store original page data
    const [allOrders, setAllOrders] = useState([]);
    const [allOrdersLoaded, setAllOrdersLoaded] = useState(false);
    const [exporting, setExporting] = useState(false);

    // Refs for debouncing and request cancellation
    const searchTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);

    const navigate = useNavigate();
    const { accessToken } = useAuth();

    // Calculate pagination
    const calculatePagination = (filteredData) => {
        const total = filteredData.length;
        const pages = Math.ceil(total / itemsPerPage);
        setTotalPages(pages);
        setTotalOrders(total);

        // Reset to page 1 if current page is beyond new total pages
        if (page > pages && pages > 0) {
            setPage(1);
        }
    };

    // Get current page data
    const getCurrentPageData = () => {
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredOrders.slice(startIndex, endIndex);
    };

    const applyDateFilter = (list) => {
        if (!exportStartDate && !exportEndDate) return list;
        const start = exportStartDate ? new Date(`${exportStartDate}T00:00:00`) : null;
        const end = exportEndDate ? new Date(`${exportEndDate}T23:59:59`) : null;
        return list.filter((o) => {
            const created = new Date(o.createdAt);
            if (start && created < start) return false;
            if (end && created > end) return false;
            return true;
        });
    };

    const applyAllFilters = (list) => {
        let out = list;
        if (statusFilter !== 'all') {
            out = out.filter((order) => order.status && order.status.toLowerCase() === statusFilter.toLowerCase());
        }
        out = applyDateFilter(out);
        return out;
    };

    const fetchOrders = async () => {
        try {
            setLoading(true);
            // Use management API to get all orders with status=ALL
            const result = await getManagementPendingOrders({ page: 0, size: 50, status: 'ALL' });

            // Handle paginated response
            let ordersArray, totalCount;
            if (result.content && result.totalElements) {
                // Paginated response
                ordersArray = result.content;
                totalCount = result.totalElements;
            } else {
                // Simple array response
                ordersArray = result;
                totalCount = result.length;
            }

            // Store the total count from API
            setTotalFromAPI(totalCount);

            // Fetch details for each order to get totalAmount
            const detailsPromises = ordersArray.map(async (order) => {
                try {
                    const details = await getManagementOrderById(order.id, 'ALL');
                    return { ...order, totalAmount: details.totalAmount, totalAmountToBePaid: details.totalAmountToBePaid, discountDetails: details.discountDetails };
                } catch (e) {
                    return order; // fallback if details fail
                }
            });
            let ordersWithTotals = await Promise.all(detailsPromises);
            // Sort by createdAt (newest first)
            ordersWithTotals = ordersWithTotals.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setOrders(ordersWithTotals);
            setOriginalOrders(ordersWithTotals);
            const next = applyAllFilters(ordersWithTotals);
            setFilteredOrders(next);
            calculatePagination(next);
        } catch (err) {
            if (handleApiError(err, navigate)) return;
            setError('Failed to fetch orders');
        } finally {
            setLoading(false);
        }
    };

    // Refetch on accessToken change or window focus
    useEffect(() => {
        fetchOrders();
        // Removed window focus refresh
    }, [accessToken]);

    const collectAllOrders = async (startDate) => {
        const out = [];
        let p = 0;
        let lastTotalPages = 1;
        const size = 500;
        setLoading(true);
        try {
            while (true) {
                const data = await getManagementPendingOrders({ page: p, size, status: 'ALL' });
                const content = data?.content || [];
                if (content.length === 0) break;
                out.push(...content);

                // Stop early if we reached past the start date
                if (startDate) {
                    const start = new Date(`${startDate}T00:00:00`);
                    const lastOrder = content[content.length - 1];
                    if (lastOrder && new Date(lastOrder.createdAt) < start) {
                        break;
                    }
                }

                lastTotalPages = Number(data?.totalPages || 1);
                if (p >= lastTotalPages - 1) break;
                p++;
            }
        } catch (err) {
            console.error("Error collecting all orders:", err);
            setError("Failed to load all orders for filtering");
        } finally {
            setLoading(false);
        }
        return out.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    };

    useEffect(() => {
        const run = async () => {
            if (exportStartDate || exportEndDate) {
                setLoading(true);
                const all = await collectAllOrders(exportStartDate);
                setAllOrders(all);
                setAllOrdersLoaded(true);
                const sourceOrders = all;

                const base = statusFilter === 'all' ? sourceOrders : sourceOrders.filter(o =>
                    o.status && o.status.toLowerCase() === statusFilter.toLowerCase()
                );
                const next = applyDateFilter(base);
                setFilteredOrders(next);
                calculatePagination(next);
                setPage(1);
                setLoading(false);
            } else {
                const base = statusFilter === 'all' ? originalOrders : originalOrders.filter(o =>
                    o.status && o.status.toLowerCase() === statusFilter.toLowerCase()
                );
                const next = base;
                setFilteredOrders(next);
                calculatePagination(next);
            }
        };
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exportStartDate, exportEndDate, statusFilter]);

    // Utility: check if query is likely an order ID (all digits or starts with 'ORD-')
    const isOrderId = (query) => /^\d+$/.test(query) || /^ORD-?\d+$/i.test(query);

    // Synchronous input handler with debouncing
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        // Debounce the search
        searchTimeoutRef.current = setTimeout(() => {
            performSearch(value);
        }, 300); // 300ms delay
    };

    // Perform the actual search
    const performSearch = async (query) => {
        // Check if search query is empty or just whitespace
        if (!query || !query.trim()) {
            // No search query - show all orders with status filter
            let filtered = originalOrders;
            if (statusFilter !== 'all') {
                filtered = filtered.filter(order =>
                    order.status && order.status.toLowerCase() === statusFilter.toLowerCase()
                );
            }
            setFilteredOrders(filtered);
            calculatePagination(filtered);
            setSearchLoading(false);
            return;
        }

        // Check if it's a numeric search (all digits or starts with 'ORD-')
        const isNumericSearch = /^\d+$/.test(query) || /^ORD-?\d+$/i.test(query);

        if (isNumericSearch) {
            // Use backend API for all numeric searches to find orders across all pages
            setSearchLoading(true);
            try {
                const order = await getManagementOrderById(query, 'ALL');
                // Check if this request was cancelled
                if (abortControllerRef.current?.signal.aborted) {
                    return;
                }
                if (order && order.id) {
                    const statusOk = statusFilter === 'all' || (order.status && order.status.toLowerCase() === statusFilter.toLowerCase());
                    const created = new Date(order.createdAt);
                    const start = exportStartDate ? new Date(`${exportStartDate}T00:00:00`) : null;
                    const end = exportEndDate ? new Date(`${exportEndDate}T23:59:59`) : null;
                    const dateOk = (!start || created >= start) && (!end || created <= end);
                    if (statusOk && dateOk) {
                        setFilteredOrders([order]);
                        calculatePagination([order]);
                    } else {
                        setFilteredOrders([]);
                        calculatePagination([]);
                    }
                } else {
                    setFilteredOrders([]);
                    calculatePagination([]);
                }
            } catch (err) {
                // Check if this request was cancelled
                if (abortControllerRef.current?.signal.aborted) {
                    return;
                }
                setFilteredOrders([]);
                calculatePagination([]);
            }
            setSearchLoading(false);
        } else {
            // Client-side filtering for non-numeric queries
            let filtered = orders;
            filtered = filtered.filter(order =>
                String(order.id).toLowerCase().includes(query.toLowerCase()) ||
                (order.type && order.type.toLowerCase().includes(query.toLowerCase()))
            );
            filtered = applyAllFilters(filtered);
            setFilteredOrders(filtered);
            calculatePagination(filtered);
            setSearchLoading(false);
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const handleStatusFilterChange = (e) => {
        const nextStatus = e.target.value;
        setStatusFilter(nextStatus);

        // Use correct source
        const source = exportStartDate || exportEndDate
            ? (allOrdersLoaded ? allOrders : originalOrders)
            : originalOrders;

        let out = source;
        if (nextStatus !== 'all') {
            out = out.filter((order) => order.status && order.status.toLowerCase() === nextStatus.toLowerCase());
        }
        out = applyDateFilter(out);
        setFilteredOrders(out);
        calculatePagination(out);
    };

    const handleViewDetails = async (orderId) => {
        setDetailsError(null);
        setDetailsLoading(true);
        try {
            const details = await getManagementOrderById(orderId, 'ALL');
            setSelectedOrder(details);
        } catch (err) {
            if (handleApiError(err, navigate)) return;
            setDetailsError('Failed to fetch order details');
            setSelectedOrder(null);
        } finally {
            setDetailsLoading(false);
        }
    };

    // Fetch existing attachments for the selected order
    const fetchExistingAttachments = async () => {
        if (!selectedOrder?.id) return;
        try {
            setExistingAttachmentsLoading(true);
            const data = await getOrderAttachments(selectedOrder.id);
            setExistingAttachments(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Failed to load attachments:', e);
        } finally {
            setExistingAttachmentsLoading(false);
        }
    };

    // Trigger attachments fetch whenever a new order is selected
    useEffect(() => {
        if (selectedOrder?.id) {
            fetchExistingAttachments();
        } else {
            setExistingAttachments([]);
        }
    }, [selectedOrder]);

    const handleBackToList = () => {
        setSelectedOrder(null);
        setDetailsError(null);
    };

    const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
    const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

    // Don't return early for loading/error - show header and search bar always

    const currentPageData = getCurrentPageData();

    const handleExportCSV = async () => {
        setExporting(true);
        try {
            const toDateObj = (d) => (d ? new Date(`${d}T23:59:59`) : null);
            const fromDateObj = (d) => (d ? new Date(`${d}T00:00:00`) : null);
            const start = fromDateObj(exportStartDate);
            const end = toDateObj(exportEndDate);
            const csvEscape = (v) => {
                const s = v == null ? '' : String(v).replace(/"/g, '""');
                return `"${s}"`;
            };
            const warehouseMap = {
                'EBL_WH': 'ERBIL',
                'DUH_WH': 'DUHOK',
                'SUL_WH': 'SULAYMANIYAH',
                'KRK_WH': 'KIRKUK',
                'MSL_WH': 'MOSUL',
                'BGD_WH': 'BAGHDAD'
            };
            const getCityName = (wh) => warehouseMap[wh] || wh;

            const collectAll = async () => {
                const out = [];
                let p = 0;
                let lastTotalPages = 1;
                const size = 100;
                while (true) {
                    const data = await getManagementPendingOrders({ page: p, size, status: 'ALL' });
                    const content = data?.content || [];
                    out.push(...content);
                    lastTotalPages = Number(data?.totalPages || 1);
                    if (p >= lastTotalPages - 1) break;
                    p++;
                }
                return out;
            };
            const source = await collectAll();
            const filtered = source.filter((o) => {
                const created = new Date(o.createdAt);
                if (start && created < start) return false;
                if (end && created > end) return false;
                if (statusFilter !== 'all' && o.status && o.status.toLowerCase() !== statusFilter.toLowerCase()) return false;
                return true;
            });
            const detailsList = filtered; // Use filtered list directly
            // Removed client fetching to optimize performance
            const clientNameMap = {};

            const allDenoms = new Set([
                ...Object.keys(SCRATCH_CARD_PRICES),
                ...Object.keys(EPIN_PRICES)
            ]);
            const scratchAgg = {};
            const epinAgg = {};
            const topupAgg = {};

            detailsList.forEach((detail, idx) => {
                const o = detail;
                const dealer = o.assignedDealer || o.dealerName || '';
                if (detail && Array.isArray(detail.scratchCardOrders) && detail.scratchCardOrders.length > 0) {
                    detail.scratchCardOrders.forEach((item) => {
                        const denom = item.denomination || '';
                        const qty = Number(item.quantity || 0);
                        const warehouse = item.warehouse || '';
                        if (denom) allDenoms.add(denom);
                        const key = `${o.id}|${dealer}|${warehouse}`;
                        if (!scratchAgg[key]) scratchAgg[key] = { orderId: o.id, dealer, warehouse, counts: {}, total: 0, dateMin: null };
                        scratchAgg[key].counts[denom] = (scratchAgg[key].counts[denom] || 0) + qty;
                        const price = SCRATCH_CARD_PRICES[denom] || Number(denom) || 0;
                        scratchAgg[key].total += (price * qty) || 0;
                        const created = new Date(o.createdAt);
                        if (!scratchAgg[key].dateMin || created < scratchAgg[key].dateMin) scratchAgg[key].dateMin = created;
                    });
                }
                if (detail && Array.isArray(detail.epinOrders) && detail.epinOrders.length > 0) {
                    detail.epinOrders.forEach((item) => {
                        const denom = item.denomination || '';
                        const qty = Number(item.quantity || 0);
                        const epClientId = item.clientId || o.clientId || '';
                        const epClientName = clientNameMap[epClientId] || '';
                        if (denom) allDenoms.add(denom);
                        const key = `${o.id}|${dealer}|${epClientId}`;
                        if (!epinAgg[key]) epinAgg[key] = { orderId: o.id, dealer, clientName: epClientName, counts: {}, total: 0, dateMin: null };
                        epinAgg[key].counts[denom] = (epinAgg[key].counts[denom] || 0) + qty;
                        const price = EPIN_PRICES[denom] || Number(denom) || 0;
                        epinAgg[key].total += (price * qty) || 0;
                        const created = new Date(o.createdAt);
                        if (!epinAgg[key].dateMin || created < epinAgg[key].dateMin) epinAgg[key].dateMin = created;
                    });
                }
                const eTopUp = detail && (detail.eTopUp || detail.etopUpOrder);
                const hasTopUp = (eTopUp && eTopUp.amount) || (detail && detail.etopUpAmount);
                if (hasTopUp) {
                    const amount = (eTopUp && eTopUp.amount) || detail.etopUpAmount || 0;
                    const totalWithFee = detail.etopUpAmountToBePaid || Math.round(amount * 1.025);
                    const key = `${o.id}|${dealer}`;
                    if (!topupAgg[key]) topupAgg[key] = { orderId: o.id, dealer, amountTotal: 0, payableTotal: 0, count: 0, dateMin: null };
                    topupAgg[key].amountTotal += Number(amount) || 0;
                    topupAgg[key].payableTotal += Number(totalWithFee) || 0;
                    topupAgg[key].count += 1;
                    const created = new Date(o.createdAt);
                    if (!topupAgg[key].dateMin || created < topupAgg[key].dateMin) topupAgg[key].dateMin = created;
                }
            });

            const sortDenoms = (arr) => {
                return arr.sort((a, b) => {
                    const na = Number(a);
                    const nb = Number(b);
                    if (!isNaN(na) && !isNaN(nb)) return na - nb;
                    return String(a).localeCompare(String(b));
                });
            };
            const denomCols = sortDenoms(Array.from(allDenoms));
            const headers = [
                'Order ID',
                'Order Type',
                'Dealer',
                'Client Name',
                ...denomCols.map((d) => String(d)),
                'E-topup amount',
                'Total',
                'Amount to be paid',
                'Date',
                'Wharehouse'
            ];

            const rows = [];
            Object.keys(scratchAgg).forEach((key) => {
                const item = scratchAgg[key];
                const dealerEsc = csvEscape(item.dealer);
                const orderIdEsc = csvEscape(item.orderId);
                const warehouseEsc = csvEscape(getCityName(item.warehouse || ''));
                const cells = [orderIdEsc, csvEscape('SCRATCH_CARD'), dealerEsc, csvEscape('')];
                let faceTotal = 0;
                denomCols.forEach((d) => {
                    const v = item.counts[d] || 0;
                    cells.push(csvEscape(v ? v : ''));
                    faceTotal += v * Number(d);
                });
                const grand = item.total || 0;
                const dateStr = item.dateMin ? formatDateTime(item.dateMin) : '';
                cells.push(csvEscape(''), csvEscape(faceTotal), csvEscape(grand), csvEscape(dateStr), warehouseEsc);
                rows.push(cells);
            });
            Object.keys(epinAgg).forEach((key) => {
                const item = epinAgg[key];
                const dealerEsc = csvEscape(item.dealer);
                const clientEsc = csvEscape(item.clientName || '');
                const orderIdEsc = csvEscape(item.orderId);
                const cells = [orderIdEsc, csvEscape('EPIN'), dealerEsc, clientEsc];
                let faceTotal = 0;
                denomCols.forEach((d) => {
                    const v = item.counts[d] || 0;
                    cells.push(csvEscape(v ? v : ''));
                    faceTotal += v * Number(d);
                });
                const grand = item.total || 0;
                const dateStr = item.dateMin ? formatDateTime(item.dateMin) : '';
                cells.push(csvEscape(''), csvEscape(faceTotal), csvEscape(grand), csvEscape(dateStr), csvEscape(''));
                rows.push(cells);
            });
            Object.keys(topupAgg).forEach((key) => {
                const t = topupAgg[key];
                const dealerEsc = csvEscape(t.dealer);
                const orderIdEsc = csvEscape(t.orderId);
                const cells = [orderIdEsc, csvEscape('ETOPUP'), dealerEsc, csvEscape('')];
                denomCols.forEach(() => cells.push(csvEscape('')));
                const dateStr = t.dateMin ? formatDateTime(t.dateMin) : '';
                cells.push(csvEscape(t.amountTotal || 0), csvEscape(t.amountTotal || 0), csvEscape(t.payableTotal || 0), csvEscape(dateStr), csvEscape(''));
                rows.push(cells);
            });

            const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const name = `orders_${exportStartDate || 'all'}_${exportEndDate || 'all'}.csv`;
            a.href = url;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            if (handleApiError(err, navigate)) return;
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Order History</h1>
                <p className="text-gray-600">View and manage all your order history</p>
            </div>

            {!selectedOrder && (
                <>
                    {/* Filters */}
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Search Orders
                                </label>
                                <input
                                    type="text"
                                    placeholder="Search by Order ID..."
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {searchLoading && (
                                    <div className="mt-2 text-sm text-gray-500">Searching...</div>
                                )}
                            </div>
                            <div className="md:w-64">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                                <DateRangePicker
                                    value={{ startDate: exportStartDate, endDate: exportEndDate }}
                                    onApply={(s, e) => {
                                        setExportStartDate(s);
                                        setExportEndDate(e);
                                    }}
                                    className="w-full"
                                />
                            </div>
                            <div className="md:w-48">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Status Filter
                                </label>
                                <select
                                    value={statusFilter}
                                    onChange={handleStatusFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="all">All Status</option>
                                    <option value="pending">Pending</option>
                                    <option value="processing">Processing</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Results count */}
                    <div className="text-sm text-gray-600 mb-4">
                        Showing {currentPageData.length} of {totalOrders} total orders
                    </div>
                    <div className="flex items-end justify-end gap-3 mb-4">
                        {exporting ? (
                            <div className="px-4 py-2 bg-blue-600 text-white rounded-md flex items-center justify-center">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={handleExportCSV}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                            >
                                Export CSV
                            </button>
                        )}
                    </div>
                </>
            )
            }

            {
                selectedOrder ? (
                    <div>
                        <button
                            className="mb-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-800"
                            onClick={handleBackToList}
                        >
                            ← Back to List
                        </button>
                        {detailsLoading ? (
                            <div className="flex justify-center items-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : detailsError ? (
                            (
                                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">{detailsError}</div>
                            )
                        ) : (
                            <OrderDetailsView order={selectedOrder}>
                                <div className="mt-8">
                                    <h3 className="text-lg font-semibold mb-3">Existing Attachments</h3>
                                    {existingAttachmentsLoading ? (
                                        <div className="text-sm text-gray-600">Loading attachments...</div>
                                    ) : existingAttachments.length === 0 ? (
                                        <div className="text-sm text-gray-600">No attachments available.</div>
                                    ) : (
                                        <ul className="divide-y divide-gray-200 bg-white border border-gray-200 rounded-md">
                                            {existingAttachments.map((att) => (
                                                <li key={att.id} className="flex items-center justify-between px-3 py-2">
                                                    <div className="text-sm text-gray-800 truncate mr-3">
                                                        <span className="text-gray-600 mr-2">#{att.id}</span>
                                                        <span className="uppercase text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">{att.contentType?.split('/')[1] || att.contentType || 'FILE'}</span>
                                                        <span className="ml-2 text-xs text-gray-500">({att.size || '—'})</span>
                                                    </div>
                                                    <div>
                                                        {att.link && (
                                                            <a
                                                                href={`${process.env.REACT_APP_API_URL}${att.link.replace('/api', '')}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 hover:text-blue-700 text-sm"
                                                            >
                                                                View
                                                            </a>
                                                        )}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </OrderDetailsView>
                        )}
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            {loading ? (
                                <div className="flex justify-center items-center h-32">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                </div>
                            ) : error ? (
                                (
                                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-center">{error}</div>
                                )
                            ) : filteredOrders.length === 0 ? (
                                <div className="p-8 text-center">
                                    <p className="text-gray-500 text-lg">No orders found</p>
                                </div>
                            ) : (
                                <>
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Order ID
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Total
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Created
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {currentPageData.map((order) => (
                                                <tr key={order.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {order.id}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {(() => {
                                                            const details = getDiscountDetails(order);
                                                            if (!details) return <span className="text-gray-400">N/A</span>;

                                                            const hasDiscount = details.discountByAmount > 0 || details.discountByPercentage > 0;

                                                            if (hasDiscount) {
                                                                return (
                                                                    <div className="flex flex-col">
                                                                        <span className="text-xs text-gray-400 line-through">
                                                                            IQD {Number(details.paidPrice).toLocaleString()}
                                                                        </span>
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="font-bold text-gray-900">
                                                                                IQD {Number(details.paidAmountAfterDiscount).toLocaleString()}
                                                                            </span>
                                                                            <span className="text-xs font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-100 whitespace-nowrap">
                                                                                -{details.discountByPercentage > 0
                                                                                    ? `${details.discountByPercentage}%`
                                                                                    : `IQD ${Number(details.discountByAmount).toLocaleString()}`}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }

                                                            return `IQD ${Number(details.paidAmountAfterDiscount).toLocaleString()}`;
                                                        })()}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                                                            {order.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {formatDateTime(order.createdAt)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        <button className="text-blue-600 hover:text-blue-900 mr-3" onClick={() => handleViewDetails(order.id)}>
                                                            View Details
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/* Pagination Bar */}
                                    {totalOrders >= 10 && (
                                        <div className="flex items-center justify-between mt-6 mb-2 w-full">
                                            <button
                                                onClick={handlePrevPage}
                                                disabled={page === 1}
                                                className={`flex items-center px-3 py-1 border rounded mr-2 text-base font-medium transition-colors duration-200 ${page === 1 ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                                style={{ minWidth: 80 }}
                                            >
                                                <span className="mr-1">&#8592;</span> Prev
                                            </button>
                                            <div className="flex space-x-2 justify-center flex-1">
                                                {(() => {
                                                    const pages = [];
                                                    if (totalPages <= 5) {
                                                        for (let p = 1; p <= totalPages; p++) {
                                                            pages.push(p);
                                                        }
                                                    } else {
                                                        if (page <= 3) {
                                                            pages.push(1, 2, 3, 4, '...', totalPages);
                                                        } else if (page >= totalPages - 2) {
                                                            pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                                                        } else {
                                                            pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                                                        }
                                                    }
                                                    return pages.map((p, idx) =>
                                                        p === '...'
                                                            ? <span key={"ellipsis-" + idx} className="w-7 h-7 flex items-center justify-center text-base text-gray-400">...</span>
                                                            : <button
                                                                key={p}
                                                                onClick={() => setPage(p)}
                                                                className={`w-7 h-7 rounded text-base font-medium transition-colors duration-200 ${p === page ? 'bg-gray-200 text-gray-900' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                                                            >
                                                                {p}
                                                            </button>
                                                    );
                                                })()}
                                            </div>
                                            <button
                                                onClick={handleNextPage}
                                                disabled={page === totalPages}
                                                className={`flex items-center px-3 py-1 border rounded ml-2 text-base font-medium transition-colors duration-200 ${page === totalPages ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                                style={{ minWidth: 80 }}
                                            >
                                                Next <span className="ml-1">&#8594;</span>
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default SupplierOrderHistory;
